import React, { useMemo } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack } from "@mui/system";

import { SecurityId } from "@fartherfinance/frontend/api/Types";
import {
  SWEEP_ACCOUNT_SECURITY_SYMBOLS,
  SweepAccountSecuritySymbol,
} from "@fartherfinance/frontend/constants/Securities";

import { ExclusionsList } from "../helpers";
import Switch from "@src/sharedComponents/Switch/Switch";
import { Cell, Row } from "@src/sharedComponents/Table/Types";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";
import { toCurrency } from "@src/yellowstone/views/Dashboard/DashboardDailyTrading/components/ProposalDetails/ProposalDetails.utils";

import { HoldingWithExclusionStatus } from "./usePortfolioHoldingsWithExcludedSecurities";

import styles from "../DoNotSellModal.module.css";

const SORT_PRIORITY = {
  inTLHSwap: "0",
  notInDoNotSellList: "1",
  inDoNotSellList: "2",
} as const;

export type DoNotSellColumns =
  | "Security"
  | "Account"
  | "Value"
  | "Quantity"
  | "Do not sell";

const columns: NonEmptyArray<DoNotSellColumns> = [
  "Security",
  "Account",
  "Value",
  "Quantity",
  "Do not sell",
];

const tooltipText =
  "This security cannot be marked do not sell because it is part of an ongoing Tax Loss Harvesting (TLH) swap.";

const notTickerTooltipText = "This security does not have a ticker.";

const noSecurityIdTooltipText =
  "This security is not available because we don't have a record in our systems.";

const sweepAccountTooltipText =
  "This security cannot be marked as do not sell because it is a cash sweep vehicle.";

const useDoNotSellTableData = (
  securities: HoldingWithExclusionStatus[],
  localExclusions: ExclusionsList,
  toggleExclusion: (sec: SecurityId, onOrOff: boolean) => void
) => {
  const tableRowData: Row<DoNotSellColumns, Cell>[] = useMemo(() => {
    return securities.map((row, index) => {
      return {
        key: index.toString(),
        Security: {
          fullValue: row.ticker ?? "ZZZ",
          value: (
            <SecurityCell
              securityName={row.ticker}
              description={
                row.securityDetails?.description ||
                row.securityDetails?.name ||
                row.assetName
              }
              securityId={row.securityId}
            />
          ),
        },
        Account: {
          fullValue: row.account || "",
          value: (
            <Text
              variant="body"
              color="textSubtle"
              className={styles.accountName}
            >
              {row.account || ""}
            </Text>
          ),
        },
        Value: {
          fullValue: row.marketValue?.toString() || "",
          value: (
            <Text variant="body" className={styles.valueColumn}>
              {row.marketValue ? toCurrency(row.marketValue) : "--"}
            </Text>
          ),
        },
        Quantity: {
          fullValue: row.quantity?.toString() || "",
          value: (
            <Text variant="body" className={styles.quantityColumn}>
              {row.quantity?.toString() || "--"}
            </Text>
          ),
        },
        "Do not sell": {
          // for sorting purposes
          fullValue: row.isTLHSecurity
            ? SORT_PRIORITY.inTLHSwap
            : localExclusions[row.securityId] ?? row.isExcluded // nullish coalescing so the localExclusions is the source of truth
            ? SORT_PRIORITY.inDoNotSellList
            : SORT_PRIORITY.notInDoNotSellList,
          value: (
            <DoNotSellSwitch
              isTLHSecurity={row.isTLHSecurity}
              isSweepAccountSecurity={row.isSweepAccountSecurity}
              tooltipText={tooltipText}
              isInDoNoSellList={row.isExcluded || false}
              securityId={row.securityId}
              clientDoNotSellList={localExclusions}
              toggleSecurity={toggleExclusion}
            />
          ),
        },
      };
    });
  }, [localExclusions, securities, toggleExclusion]);

  return useMemo(
    () => ({
      columns: columns,
      rows: tableRowData,
    }),
    [tableRowData]
  );
};

export default useDoNotSellTableData;

type SecurityCellProps = {
  securityName?: string | null;
  description?: string | null;
  securityId?: string | null;
};

const SecurityCell: React.FC<SecurityCellProps> = ({
  securityName,
  description,
  securityId,
}) => {
  const isSecurityIdMissing = !securityId;

  // Check if this security is a sweep account/money market fund
  const isSweepAccount = securityName
    ? SWEEP_ACCOUNT_SECURITY_SYMBOLS.has(
        securityName.toUpperCase() as SweepAccountSecuritySymbol
      )
    : false;

  const tooltipContent = isSecurityIdMissing
    ? noSecurityIdTooltipText
    : securityName === undefined
    ? notTickerTooltipText
    : isSweepAccount
    ? sweepAccountTooltipText
    : "";

  return (
    <Stack spacing={2} direction={"row"}>
      <Tooltip tooltipText={tooltipContent} placement={"top"}>
        <Text
          variant="body"
          color={
            isSecurityIdMissing || securityName === undefined || isSweepAccount
              ? "textSubtle"
              : "text"
          }
          className={styles.securityName}
        >
          {securityName ?? "--"}
        </Text>
      </Tooltip>

      <Tooltip tooltipText={description} placement={"top"}>
        <Text
          variant="body"
          color="textSubtle"
          className={styles.securityDescription}
        >
          {description}
        </Text>
      </Tooltip>
    </Stack>
  );
};

type DoNotSellSwitchProps = {
  isTLHSecurity: boolean;
  isSweepAccountSecurity: boolean;
  tooltipText: string;
  isInDoNoSellList: boolean;
  securityId: SecurityId;
  clientDoNotSellList: ExclusionsList;
  toggleSecurity: (sec: SecurityId, onOrOff: boolean) => void;
};

const DoNotSellSwitch: React.FC<DoNotSellSwitchProps> = ({
  isTLHSecurity,
  isSweepAccountSecurity,
  tooltipText,
  isInDoNoSellList,
  securityId,
  clientDoNotSellList,
  toggleSecurity,
}) => {
  const handleChange = () => {
    // Check if security ID is available
    if (!securityId) {
      return;
    }

    // Don't allow toggling sweep account securities
    if (isSweepAccountSecurity) {
      return;
    }

    const isSecurityToggledON =
      clientDoNotSellList[securityId] ?? isInDoNoSellList;
    toggleSecurity(securityId, !isSecurityToggledON);
  };

  const isSwitchOn = securityId
    ? clientDoNotSellList[securityId] ?? isInDoNoSellList
    : false;
  const isSecurityIdMissing = !securityId;

  // Determine tooltip text based on conditions
  const displayTooltipText = isTLHSecurity
    ? tooltipText
    : isSecurityIdMissing
    ? noSecurityIdTooltipText
    : isSweepAccountSecurity
    ? sweepAccountTooltipText
    : "";

  // Determine if we should show the info icon
  const showInfoIcon =
    isTLHSecurity || isSecurityIdMissing || isSweepAccountSecurity;

  // Determine if the switch should be disabled
  const isDisabled =
    isTLHSecurity || isSecurityIdMissing || isSweepAccountSecurity;

  return (
    <Tooltip placement={"top"} tooltipText={displayTooltipText}>
      <Stack direction={"row"} spacing={2}>
        <InfoOutlinedIcon
          style={{
            width: "14px",
            height: "14px",
            visibility: showInfoIcon ? undefined : "hidden",
          }}
        />
        <Switch onClick={handleChange} disabled={isDisabled} on={isSwitchOn} />
      </Stack>
    </Tooltip>
  );
};
